import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactNode } from 'react';

import { useLocalization } from '../../../localization';
import { MODAL_WIDTH } from 'constants/ui/style';

export type ModalProps = {
  isOpen: boolean;
  toggleModal: () => void;
  closable?: boolean;
};

export type ModalContentProps = {
  modalTitle?: ReactNode;
  confirmBtnLabel?: string;
  cancelBtnLabel?: string;
  isLoading?: boolean;
  width?: number;
  onConfirm?: () => void;
  onCancel?: () => void;
  children?: React.ReactNode;
  hideFooter?: boolean;
  disableConfirm?: boolean;
};

export type Props = ModalProps & Partial<ModalContentProps>;

const Modal = (props: Props) => {
  const { t } = useLocalization();
  const {
    confirmBtnLabel = t('common.actions.confirm'),
    cancelBtnLabel = t('common.actions.cancel'),
    isLoading = false,
    width = MODAL_WIDTH.sm,
    onConfirm,
    onCancel,
    modalTitle = '',
    children,
    isOpen,
    toggleModal,
    hideFooter,
    closable = true,
    disableConfirm = false,
  } = props;

  const handleCancel = () => {
    if (onCancel) {
      onCancel?.();
    } else {
      toggleModal?.();
    }
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  const handleClose = () => {
    if (!isLoading) toggleModal?.();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: `${width}px`,
            maxWidth: '100%',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 500 }}>{modalTitle}</Typography>
        {closable && (
          <IconButton aria-label="close" onClick={handleClose} disabled={isLoading} edge="end" sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>{children}</DialogContent>

      {!hideFooter && (
        <DialogActions
          sx={{ boxShadow: '0px -1px 1px rgba(0, 0, 0, 0.1)', padding: '1rem 1.5rem', display: 'flex', gap: 0.5 }}
        >
          <Button sx={{ minWidth: 'fit-content' }} disabled={isLoading} variant="outlined" onClick={handleCancel}>
            {cancelBtnLabel}
          </Button>
          <Button
            variant={disableConfirm ? 'outlined' : 'contained'}
            sx={{ minWidth: 'fit-content' }}
            loading={isLoading}
            onClick={handleConfirm}
            disabled={disableConfirm}
          >
            {confirmBtnLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
